import React, { memo } from 'react';
import { StyleSheet, View } from 'react-native';

import { LI, UL } from '@expo/html-elements';

import { useA11ySelector } from '@sg/a11y-helpers';
import { theme, useResponsive } from '@sg/garnish';

import { type AddressWithoutIds, type LocationAddress } from '../../../locations.types';
import { LocationSearchAddress } from '../LocationSearchAddress';

export const LocationSearchPredictionAddressesList = memo(
  (props: LocationSearchPredictionAddressesListProps) => {
    const { addresses, onPress, withBackground = true } = props;

    const { match } = useResponsive();
    const a11ySelector = useA11ySelector('locations.delivery.addresses-container'); // prettier-ignore

    // ─── Styles ────────────────────────────────────────────────────────

    const containerStyles = [
      match([styles.containerXS, styles.containerSM]),
      withBackground ? match([styles.containerXSBackground, undefined]) : undefined,
    ];

    // ─────────────────────────────────────────────────────────────────────

    return (
      <View style={containerStyles} {...a11ySelector.props}>
        <UL>
          {addresses.map((address) => {
            return (
              <LI key={address.id}>
                <LocationSearchAddress
                  variation="prediction-address"
                  address={address}
                  onPress={onPress}
                />
              </LI>
            );
          })}
        </UL>
      </View>
    );
  },
);

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  containerXS: {
    ...StyleSheet.absoluteFill,
    zIndex: 1,
    padding: theme.spacing['2'],
  },
  containerXSBackground: {
    backgroundColor: theme.colors.APP_BACKGROUND,
  },
  containerSM: {
    padding: theme.spacing['2'],
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type LocationSearchPredictionAddressesListProps = {
  addresses: LocationAddress[] | readonly LocationAddress[];
  onPress: (predictedAddress: AddressWithoutIds) => void;
  withBackground?: boolean;
};
